import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, ArchiveRestore, Plus, FileText, Search, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { HolderSwitcher } from '@/components/HolderSwitcher';
import { ArcheHeader } from '../../../components/ArcheHeader';
import {
  useArchiveEnvelope,
  useCreateEnvelope,
  useEnvelopes,
  useUnarchiveEnvelope,
} from '../hooks';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { DocumentSignSettingsDialog } from '../components';
import {
  DEFAULT_DOCUMENT_SIGN_SETTINGS,
  DOCUMENT_SIGN_ARCHE_ID,
  PLATFORM_DEFAULT_ENVELOPE_MESSAGE,
  PLATFORM_DEFAULT_ENVELOPE_TITLE,
} from '../settings';
import type { DocumentSignEnvelopeResponse, DocumentSignEnvelopeStatus } from '@/lib/api';

const STATUS_FILTERS: {
  key: string;
  label: string;
  statuses: DocumentSignEnvelopeStatus[];
}[] = [
  { key: 'all', label: 'All', statuses: [] },
  { key: 'drafts', label: 'Drafts', statuses: ['draft'] },
  { key: 'out', label: 'Out for signature', statuses: ['sent', 'in_progress'] },
  { key: 'completed', label: 'Completed', statuses: ['completed'] },
  { key: 'voided', label: 'Voided', statuses: ['voided'] },
  { key: 'declined', label: 'Declined', statuses: ['declined'] },
  { key: 'expired', label: 'Expired', statuses: ['expired'] },
];

/**
 * Archive lives in the same pill bar but is its own dimension — it flips
 * the underlying `archiveScope` query rather than narrowing by status.
 * Keyed separately so we can detect it in the filter logic without
 * conflating it with a real status.
 */
const ARCHIVED_FILTER_KEY = 'archived';

const PAGE_SIZE = 20;

export function EnvelopesListPage() {
  const navigate = useNavigate();
  const { holderId, setHolderId } = useSelectedHolder();
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set(['all']));
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, updateSettings] = useArcheSettings(
    DOCUMENT_SIGN_ARCHE_ID,
    DEFAULT_DOCUMENT_SIGN_SETTINGS,
  );

  const showingArchived = activeFilters.has(ARCHIVED_FILTER_KEY);
  // Status filter doesn't apply when the archive view is showing — archived
  // envelopes span every status. The "All" pill is similarly a no-op.
  const statuses =
    showingArchived || activeFilters.has('all')
      ? undefined
      : Array.from(
          new Set(
            STATUS_FILTERS.filter((f) => activeFilters.has(f.key)).flatMap((f) => f.statuses),
          ),
        );

  const { envelopes, total, loading, error } = useEnvelopes({
    holderId,
    archiveScope: showingArchived ? 'archived' : 'unarchived',
    status: statuses && statuses.length > 0 ? statuses : undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const createEnvelope = useCreateEnvelope();
  const archiveEnvelope = useArchiveEnvelope();
  const unarchiveEnvelope = useUnarchiveEnvelope();

  async function handleCreate() {
    if (!holderId) return;
    // User-configured settings win; platform defaults are the fallback so
    // the owner doesn't have to type anything to get a sensible envelope.
    // The message is an actual default VALUE (not a placeholder) — the
    // user can edit or clear it; clearing sticks.
    const userMessage = settings.defaultMessage.trim();
    const userTitle = settings.defaultTitle.trim();
    try {
      const envelope = await createEnvelope.mutateAsync({
        holderId,
        title: userTitle || PLATFORM_DEFAULT_ENVELOPE_TITLE,
        message: userMessage || PLATFORM_DEFAULT_ENVELOPE_MESSAGE,
      });
      navigate(`/a/arche_document_sign/envelope/${envelope.envelopeId}`);
    } catch {
      // Failures are captured in createEnvelope.error and rendered as an
      // inline banner above the filter pills — no popup, no toast. The
      // mutateAsync re-throw lets us avoid the navigate on failure.
    }
  }

  function selectFilter(key: string) {
    // Single-select pills — clicking a filter switches to it. Earlier
    // versions allowed multi-select (clicking a second pill added it to
    // the active set), but the multi-highlight behavior was confusing and
    // visually indistinguishable from "things accumulate while clicking",
    // especially when secondary categories are empty.
    setPage(0);
    setActiveFilters(new Set([key]));
  }

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={holderId} onChange={setHolderId} />
        </ArcheHeader>
        <div className="max-w-5xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  // No workspace selected — show the "choose a workspace" empty state and
  // keep the chip in the header so the user can pick one. Mirrors the
  // Fias 360 / Fias Leads no-holder gate.
  if (!holderId) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={holderId} onChange={setHolderId} />
        </ArcheHeader>
        <NoHolderEmptyState holderId={holderId} setHolderId={setHolderId} />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={holderId} onChange={setHolderId} />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSettingsOpen(true)}
          aria-label="Document Sign settings"
        >
          <Settings className="h-4 w-4" />
        </Button>
        <Button onClick={handleCreate} disabled={createEnvelope.isPending || !holderId}>
          <Plus className="h-4 w-4 mr-2" />
          {createEnvelope.isPending ? 'Creating…' : 'New envelope'}
        </Button>
      </ArcheHeader>
      <div className="max-w-5xl mx-auto p-6 space-y-4 w-full">
        {createEnvelope.error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {createEnvelope.error instanceof Error
              ? createEnvelope.error.message
              : 'Failed to create envelope'}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => {
            const active = activeFilters.has(f.key);
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => selectFilter(f.key)}
                aria-pressed={active}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-muted hover:bg-accent/40'
                }`}
              >
                {f.label}
              </button>
            );
          })}
          {/*
           * Archived sits in the same pill bar but flips the underlying
           * archiveScope rather than the status filter. Single-select with
           * the rest, so picking it deselects whichever status pill was
           * active.
           */}
          <button
            type="button"
            onClick={() => selectFilter(ARCHIVED_FILTER_KEY)}
            aria-pressed={showingArchived}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              showingArchived
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-muted hover:bg-accent/40'
            }`}
          >
            Archived
          </button>
        </div>

        <form onSubmit={applySearch} className="flex gap-2" role="search">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by title or recipient email"
              className="pl-9"
              aria-label="Search envelopes"
              maxLength={200}
            />
          </div>
          <Button type="submit" variant="outline">
            Search
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setPage(0);
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : envelopes.length === 0 ? (
          <EmptyState
            search={search}
            activeFilterKey={Array.from(activeFilters)[0] ?? 'all'}
            onCreate={handleCreate}
            disabled={createEnvelope.isPending}
          />
        ) : (
          <>
            <div className="border rounded-lg divide-y" role="list">
              {envelopes.map((env) => (
                <EnvelopeRow
                  key={env.envelopeId}
                  envelope={env}
                  isArchived={showingArchived}
                  onArchive={() => archiveEnvelope.mutate(env.envelopeId)}
                  onUnarchive={() => unarchiveEnvelope.mutate(env.envelopeId)}
                  archivePending={archiveEnvelope.isPending || unarchiveEnvelope.isPending}
                  onClick={() =>
                    navigate(
                      env.status === 'draft'
                        ? `/a/arche_document_sign/envelope/${env.envelopeId}`
                        : `/a/arche_document_sign/envelope/${env.envelopeId}/detail`,
                    )
                  }
                />
              ))}
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                Showing {showingFrom}–{showingTo} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <span className="self-center" aria-live="polite">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page + 1 >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <DocumentSignSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onSave={updateSettings}
      />
    </div>
  );
}

/**
 * Shown when no workspace is selected. Mirrors the Fias 360 / Fias Leads
 * pattern — the header chip is still visible above so the user can pick
 * a workspace there; the embedded picker is a convenience for first-run.
 */
function NoHolderEmptyState({
  holderId,
  setHolderId,
}: {
  holderId: string | null;
  setHolderId: (id: string | null) => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
      <h2 className="text-xl font-semibold text-foreground">Choose a workspace to get started</h2>
      <p className="max-w-md text-muted-foreground">
        Document Sign envelopes live in a workspace. Pick Personal to send and sign on your own
        behalf, or an organization workspace to do it on the team's behalf.
      </p>
      <HolderSwitcher selectedHolderId={holderId} onChange={setHolderId} />
    </div>
  );
}

function EmptyState({
  search,
  activeFilterKey,
  onCreate,
  disabled,
}: {
  search: string;
  activeFilterKey: string;
  onCreate: () => void;
  disabled: boolean;
}) {
  if (search) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg text-muted-foreground mb-1">No envelopes match "{search}"</p>
        <p className="text-sm text-muted-foreground">Try a different title or recipient email.</p>
      </div>
    );
  }
  if (activeFilterKey !== 'all') {
    const filter = STATUS_FILTERS.find((f) => f.key === activeFilterKey);
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-lg text-muted-foreground">
          No envelopes in {filter?.label.toLowerCase() ?? 'this state'}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <FileText className="h-12 w-12 text-muted-foreground mb-4" />
      <p className="text-lg text-muted-foreground mb-4">No envelopes yet</p>
      <Button onClick={onCreate} disabled={disabled}>
        <Plus className="h-4 w-4 mr-2" />
        Create your first envelope
      </Button>
    </div>
  );
}

function EnvelopeRow({
  envelope,
  isArchived,
  onClick,
  onArchive,
  onUnarchive,
  archivePending,
}: {
  envelope: DocumentSignEnvelopeResponse;
  isArchived: boolean;
  onClick: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  archivePending: boolean;
}) {
  // The row itself navigates on click; the archive button sits inside the
  // row as a nested click target. stopPropagation on the button prevents
  // the parent click handler from also firing — otherwise clicking
  // Archive would also navigate to the detail view.
  return (
    <div
      role="listitem"
      className="w-full p-4 hover:bg-accent/40 flex items-center justify-between gap-3"
    >
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 text-left"
        aria-label={`Open ${envelope.title || 'Untitled envelope'}`}
      >
        <div className="font-medium truncate">{envelope.title || 'Untitled envelope'}</div>
        <div className="text-xs text-muted-foreground mt-1">
          Updated {new Date(envelope.updatedAt).toLocaleString()}
        </div>
      </button>
      <div className="flex items-center gap-3">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {envelope.status.replace('_', ' ')}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            if (isArchived) onUnarchive();
            else onArchive();
          }}
          disabled={archivePending}
          aria-label={isArchived ? 'Unarchive envelope' : 'Archive envelope'}
          title={isArchived ? 'Unarchive' : 'Archive'}
        >
          {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
